import { Layout } from '@components/common'
import { getConfig } from '@framework/api'
import getSiteInfo from '@framework/common/get-site-info'
import { getPageData } from '@utils/contentful/get-page-data'
import filterCountryFromCategoryPath from '@utils/filterCountryFromCategoryPath'
import type { GetStaticPropsContext } from 'next'
import { CONTENTFUL_SPACE_IDS, loadProductInformation } from '@utils/contentful'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import take from 'lodash/take'
import {
  NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
  NEXT_PUBLIC_DOMAIN_NAME,
} from '@utils/client-side-config'
import { revalidatePeriod } from '@utils/server-side-config'
import { NextSeo, ProductJsonLd } from 'next-seo'
import { getContentfulClient } from '@utils/contentful/clients'
import { ProductPage } from '@components/product/ProductPage/ProductPage'
import { getContentfulComponents } from '@utils/contentful/contentfulComponents/pageContent'
import { mapContentfulImageAsset } from '@utils/contentful/contentfulImage'
import { IProductPage } from '@components/product/ProductPage/ProductPage.types'
import { ProductPageDataProvider } from '@components/product/ProductPage/ProductPageDataProvider'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { getProductsWithPrice } from '@utils/platform/products'
import { startRenderTransaction, finishRenderTransaction } from '@utils/tracing'
import { useRouter } from 'next/router'
import {
  isProductCustomisable,
  mapContentBlockToServiceDetails,
} from '@components/product/ProductPage/ProductPage.utils'
import { getFormSpecification } from '@utils/form/hubspot'

export async function getStaticProps({
  params,
  preview,
  locale,
}: GetStaticPropsContext<{ slug: string }>) {
  const tx = startRenderTransaction('/custom-packaging/[slug]', params.slug)

  const config = getConfig({ locale })
  const { categories } = await getSiteInfo({ config, preview })
  const pageData = await getPageData({ categories, preview })

  const contentfulClient = getContentfulClient({
    contentfulSpace: CONTENTFUL_SPACE_IDS.ECOMMERCE_1,
    preview,
  })

  /* Get Products Data */
  const product = await loadProductInformation({
    slug: params?.slug,
    client: contentfulClient,
    useLocale: true,
  })

  if (isEmpty(product)) {
    return {
      notFound: true,
    }
  }

  let hubspotPlusForm = null

  if (product?.fields?.hubspotPlusFormId) {
    hubspotPlusForm = await getFormSpecification(
      product?.fields?.hubspotPlusFormId
    )
  }

  if (!isProductCustomisable(product?.fields?.productSlug)) {
    return {
      redirect: {
        destination: `/marketplace/${product?.fields?.productSlug}`,
      },
    }
  }

  const productVariantsSales = await getProductsWithPrice(
    product?.fields?.productSlug
  )

  const productOptionLowestPrice = Math.min(
    ...productVariantsSales.map((variant) => variant.price)
  )

  const contentfulPageComponents = await getContentfulComponents({
    pageSlug: '/custom-packaging/' + product?.fields?.urlSlug,
    dataForParsers: {
      categories,
      parentSlug: 'custom-packaging',
      preview,
    },
  })

  finishRenderTransaction(tx)

  return {
    props: {
      ...pageData,
      productEntries: {
        fields: product.fields || null,
      },
      productOptionLowestPrice,
      contentfulPageComponents: contentfulPageComponents || null,
      productVariantsSales: sortBy(productVariantsSales, 'price'), // All products with the prices. So if product has zero price, it won't show,
      hubspotPlusForm,
    },
    revalidate: revalidatePeriod(), // In seconds
  }
}

export async function getStaticPaths() {
  if (!process.env.FORCE_BUILD) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const config = getConfig()
  const { categories } = await getSiteInfo({ config })
  let categoriesStock: any = {}
  categories.forEach((cat: any) => {
    if (cat.name === 'Custom Packaging') {
      categoriesStock = cat
    }
  })

  const paths = categoriesStock.children.map((cat: any) => {
    return filterCountryFromCategoryPath(cat.path)
  })
  return {
    paths: take(paths, 3),
    fallback: 'blocking',
  }
}

export default function Slug({
  productEntries,
  contentfulPageComponents,
  productVariantsSales,
  productOptionLowestPrice,
  hubspotPlusForm,
}: IProductPage) {
  const router = useRouter()
  const productSeo = productEntries?.fields?.seoModelRef?.fields || null

  return (
    <div key={router.asPath}>
      {productSeo?.canonicalUrl && (
        <NextSeo
          canonical={`https://${NEXT_PUBLIC_DOMAIN_NAME}${productSeo.canonicalUrl}`}
        />
      )}
      <ProductJsonLd
        productName={productEntries?.fields?.productName}
        images={
          productEntries?.fields?.productHeroImages?.map(
            (productImage) => `https:${productImage?.fields?.file.url}`
          ) || []
        }
        description={
          productSeo?.seoDescription ||
          documentToPlainTextString(
            productEntries?.fields?.productDescriptionRichText
          )
        }
        brand="noissue Custom"
        manufacturerName="noissue"
        manufacturerLogo={`https://${NEXT_PUBLIC_DOMAIN_NAME}/images/logo.svg`}
        aggregateOffer={{
          priceCurrency: NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
          lowPrice: productOptionLowestPrice?.toString(),
        }}
      />
      <ProductPageDataProvider
        value={{
          productVariants: productVariantsSales,
          contentfulData: {
            productName: productEntries?.fields?.productName,
            productType: productEntries?.fields?.productType,
            productDescription:
              productEntries?.fields?.productDescriptionRichText,
            productSeo: productSeo,
            productHeroImages: productEntries?.fields?.productHeroImages,
            productTags: productEntries?.fields?.productTags?.map((x) => {
              return {
                ...x?.fields,
                labelImage: mapContentfulImageAsset(x?.fields?.labelImage),
              }
            }),
            productSlug: productEntries?.fields?.productSlug,
            fieldsConfiguration: productEntries?.fields?.productSelectionFields,
            ctaLabel: productEntries?.fields?.productInfoButtonTitle,
            moq: productEntries?.fields?.productInfoMoq,
            helpText: productEntries?.fields?.productInfoFooter,
            comingSoonState: productEntries?.fields?.comingSoonState,
            enableRequestAQuoteFlow:
              productEntries?.fields?.enableRequestAQuoteFlow,
            requestAQuoteFormPath:
              productEntries?.fields?.requestAQuoteFormPath,
            alternativeDtcPagePath:
              productEntries?.fields?.alternativeDtcPagePath,
            minimumQuoteQuantityOverride:
              productEntries?.fields?.minimumQuoteQuantityOverride,
            isPlusProductAvailable:
              productEntries?.fields?.plusProductAvailabilityPerRegion,
            productPlusEditorConfig:
              productEntries?.fields?.productPlusEditorConfig,
            extraQuoteQuantityOptions:
              productEntries?.fields?.extraQuoteQuantityOptions,
            productDeliveryTypes:
              productEntries?.fields?.productDeliveryTypes?.map((entry) => {
                return {
                  ...entry.fields,
                  deliveryTypeIcon: mapContentfulImageAsset(
                    entry?.fields?.deliveryTypeIcon
                  ),
                }
              }) || [],
            productDeliveryDisclaimer:
              productEntries?.fields?.productDeliveryDisclaimer,
            onlineEditorUrl: productEntries?.fields?.onlineEditorUrl,
            correspondingPlusProductSku:
              productEntries?.fields?.correspondingPlusProductSku,
            productDetailsBadge: {
              title: productEntries?.fields?.productDetailsBadge?.fields?.h1,
              image: mapContentfulImageAsset(
                productEntries?.fields?.productDetailsBadge?.fields
                  ?.primaryImages[0]
              ),
              ctaUrl:
                productEntries?.fields?.productDetailsBadge?.fields?.ctaLink,
            },
            serviceDetails:
              productEntries?.fields?.serviceDetails
                ?.map((x) => mapContentBlockToServiceDetails(x))
                ?.filter(Boolean) || [],
            breadcrumbsConfig:
              productEntries?.fields?.breadcrumbs?.map((x) => {
                return {
                  name: x?.fields?.ctaLabel,
                  path: x?.fields?.ctaLink,
                }
              }) || [],
          },
          hubspotPlusForm,
        }}
      >
        <ProductPage
          stickySectionComponents={
            contentfulPageComponents?.slotAComponentProps
          }
          fullWidthSectionComponents={
            contentfulPageComponents?.slotBComponentProps
          }
          contentfulComponentsAnchors={
            contentfulPageComponents?.componentsAnchors
          }
        />
      </ProductPageDataProvider>
    </div>
  )
}

Slug.Layout = Layout
